import { useHandler } from "@redotech/react-util/hook";
import ChevronDown from "@redotech/redo-web/icon/chevron-down.svg";
import * as classnames from "classnames";
import {
  ForwardedRef,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  forwardRef,
  memo,
} from "react";
import * as buttonCss from "./button.module.css";
import CircleSpinner from "./circle-spinner.svg";

export namespace ButtonTheme {
  export const BRAND = Symbol("Brand");
  export const DANGER = Symbol("Danger");
  export const GHOST = Symbol("Ghost");
  export const OUTLINED = Symbol("Outlined");
  export const PRIMARY = Symbol("Primary");
  export const SOLID_LIGHT = Symbol("Solid Light");
  export const SOLID_LIGHT_BRAND = Symbol("Solid Light Brand");
  export const WARNING = Symbol("Warning");
}

export type ButtonTheme =
  | typeof ButtonTheme.BRAND
  | typeof ButtonTheme.DANGER
  | typeof ButtonTheme.GHOST
  | typeof ButtonTheme.OUTLINED
  | typeof ButtonTheme.PRIMARY
  | typeof ButtonTheme.SOLID_LIGHT
  | typeof ButtonTheme.SOLID_LIGHT_BRAND
  | typeof ButtonTheme.WARNING;

export namespace ButtonSize {
  export const MEDIUM = Symbol("normal");
  export const SMALL = Symbol("compact");
  export const EXTRA_SMALL = Symbol("extra-small");
  export const MICRO = Symbol("micro");
}

export type ButtonSize =
  | typeof ButtonSize.SMALL
  | typeof ButtonSize.EXTRA_SMALL
  | typeof ButtonSize.MEDIUM
  | typeof ButtonSize.MICRO;

export namespace ButtonBorder {
  export const DARK = Symbol("dark");
  export const LIGHT = Symbol("light");
}

export type ButtonBorder = typeof ButtonBorder.DARK | typeof ButtonBorder.LIGHT;

const themeClass = {
  [ButtonTheme.BRAND]: buttonCss.brand,
  [ButtonTheme.DANGER]: buttonCss.danger,
  [ButtonTheme.GHOST]: buttonCss.ghost,
  [ButtonTheme.OUTLINED]: buttonCss.outlined,
  [ButtonTheme.PRIMARY]: buttonCss.primary,
  [ButtonTheme.SOLID_LIGHT]: buttonCss.solidLight,
  [ButtonTheme.SOLID_LIGHT_BRAND]: buttonCss.solidLightBrand,
  [ButtonTheme.WARNING]: buttonCss.warning,
};

const sizeClass = {
  [ButtonSize.MEDIUM]: buttonCss.medium,
  [ButtonSize.SMALL]: buttonCss.small,
  [ButtonSize.MICRO]: buttonCss.micro,
  [ButtonSize.EXTRA_SMALL]: buttonCss.extraSmall,
};

const borderClass = {
  [ButtonBorder.DARK]: buttonCss.dark,
  [ButtonBorder.LIGHT]: buttonCss.light,
};

export const Button = memo(
  forwardRef(
    (
      {
        children,
        className,
        form,
        icon,
        theme = ButtonTheme.GHOST,
        disabled = false,
        onClick,
        pending = false,
        size = ButtonSize.MEDIUM,
        border = ButtonBorder.DARK,
        type = "button",
        style = undefined,
        chevron = false,
        ...rest
      }: {
        children?: ReactNode;
        className?: string;
        form?: string;
        icon?: ({ className }: { className?: string }) => ReactElement;
        theme?: ButtonTheme;
        disabled?: boolean;
        pending?: boolean;
        size?: ButtonSize;
        border?: ButtonBorder;
        style?: any;
        chevron?: boolean;
        type?: "button" | "reset" | "submit";
        onClick?: MouseEventHandler<HTMLButtonElement>;
      },
      ref: ForwardedRef<HTMLButtonElement>,
    ) => {
      const buttonClassName = classnames(
        buttonCss.button,
        themeClass[theme],
        sizeClass[size],
        borderClass[border],
        className,
        { [buttonCss.pending]: pending },
      );

      return (
        <button
          className={buttonClassName}
          disabled={disabled || pending}
          form={form}
          onClick={onClick}
          ref={ref}
          type={type}
          style={style}
          {...rest}
        >
          {pending && <CircleSpinner className={buttonCss.spinner} />}
          <div className={buttonCss.content}>
            {icon && icon({ className: buttonCss.icon })}
            {chevron ? (
              <div className={buttonCss.chevronButton}>
                {children}
                <ChevronDown className={buttonCss.chevron} />
              </div>
            ) : (
              children
            )}
          </div>
        </button>
      );
    },
  ),
);

export enum IconButtonTheme {
  LIGHT,
  DARK,
  GRAY,
}

export const IconButton = memo(
  forwardRef(
    (
      {
        children,
        disabled = false,
        onClick,
        size = ButtonSize.MEDIUM,
        theme = IconButtonTheme.DARK,
        type = "button",
        ...props
      }: {
        children: ReactNode;
        onClick?(): void;
        size?: ButtonSize;
        theme?: IconButtonTheme;
      } & React.DetailedHTMLProps<
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        HTMLButtonElement
      >,
      ref: ForwardedRef<HTMLButtonElement>,
    ) => {
      const handleClick: MouseEventHandler = useHandler((event) => {
        onClick && onClick();
        event.stopPropagation();
      });
      const baseClasses = [
        buttonCss.iconButton,
        {
          [buttonCss.dark]: theme === IconButtonTheme.DARK,
          [buttonCss.light]: theme === IconButtonTheme.LIGHT,
          [buttonCss.gray]: theme === IconButtonTheme.GRAY,
        },
        sizeClass[size],
      ];
      const classNames = props.className
        ? classnames(...baseClasses, props.className)
        : classnames(...baseClasses);

      return (
        <button
          ref={ref}
          className={classNames}
          disabled={disabled}
          type={type}
          onClick={handleClick}
          {...props}
        >
          {children}
        </button>
      );
    },
  ),
);
